import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from 'date-fns';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import TransactionItem from "@/components/transaction-item";
import type { PromoCode, PromoCodeRedemption } from "@shared/schema";
import { Twitter } from "lucide-react";

import { 
  Gift, 
  CheckCircle, 
  XCircle,
  Zap,
  Sparkles,
  Bell,
  Info,
  History,
  Loader2,
  BadgePercent
} from "lucide-react";

const promoCodeSchema = z.object({
  code: z.string().min(1, "Please enter a promo code").max(50, "Code is too long"),
});

type PromoCodeForm = z.infer<typeof promoCodeSchema>;
type PopulatedPromoCodeRedemption = PromoCodeRedemption & { promoCode?: PromoCode };

// API Functions
const fetchPublicPromoCodes = async (): Promise<PromoCode[]> => {
  const res = await fetch("/api/promocodes");
  if (!res.ok) throw new Error("Failed to fetch promo codes");
  return res.json();
};

const fetchPromoCodeRedemptions = async (): Promise<PopulatedPromoCodeRedemption[]> => {
  const res = await fetch("/api/promocodes/redemptions");
  if (!res.ok) throw new Error("Failed to fetch your redemption history");
  return res.json();
};

const redeemPromoCode = async (code: string): Promise<{ message: string, reward: number }> => {
  const res = await fetch("/api/promocodes/redeem", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Failed to redeem code");
  return data;
};

const PromoCodes = () => {
  const queryClient = useQueryClient();
  const [redeemStatus, setRedeemStatus] = useState<{status: 'success' | 'error', message: string, reward?: number} | null>(null);

  const form = useForm<PromoCodeForm>({
    resolver: zodResolver(promoCodeSchema),
    defaultValues: { code: "" },
  });

  // Data Fetching
  const { data: allPromoCodes, isLoading: isLoadingCodes } = useQuery({
    queryKey: ["publicPromoCodes"],
    queryFn: fetchPublicPromoCodes,
  });

  const { data: redeemHistory, isLoading: isLoadingHistory } = useQuery<PopulatedPromoCodeRedemption[]>({ 
    queryKey: ["promoCodeRedemptions"],
    queryFn: fetchPromoCodeRedemptions,
  });

  // Filter for truly active promo codes
  const activePromoCodes = useMemo(() => {
    if (!allPromoCodes) return [];
    const now = new Date();
    return allPromoCodes.filter(promo => {
      const isExpired = promo.expiresAt && new Date(promo.expiresAt) < now;
      const isUsedUp = promo.uses >= promo.maxUses;
      return !isExpired && !isUsedUp;
    });
  }, [allPromoCodes]);

  // Mutation for redeeming a code
  const redemptionMutation = useMutation({
    mutationFn: (code: string) => redeemPromoCode(code),
    onSuccess: (data) => {
      setRedeemStatus({ status: 'success', message: data.message, reward: data.reward });
      queryClient.invalidateQueries({ queryKey: ["publicPromoCodes"] });
      queryClient.invalidateQueries({ queryKey: ["promoCodeRedemptions"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      form.reset();
    },
    onError: (error) => {
        setRedeemStatus({ status: 'error', message: error.message });
    },
  });

  const onSubmit = (data: PromoCodeForm) => {
    setRedeemStatus(null); // Reset status on new submission
    redemptionMutation.mutate(data.code);
  };

  const handleCodeClick = (code: string) => {
    form.setValue("code", code);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="py-20 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-3">
            <Sparkles className="inline-block w-8 h-8 mr-2 text-primary"/>
            Promo Codes
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Redeem special codes for bonus tokens and exclusive rewards.
          </p>
        </motion.div>

        {/* Redeem Form - The Main Event */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-effect border-border shadow-lg max-w-2xl mx-auto">   
                <CardContent className="p-6">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                            control={form.control}
                            name="code"
                            render={({ field }) => (
                                <FormItem>
                                <FormLabel className="sr-only">Promo Code</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Gift className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            placeholder="ENTER-CODE-HERE"
                                            className="pl-12 text-center font-mono text-lg uppercase h-14 tracking-widest bg-background border border-border/50 focus:bg-background/70 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                            {...field}
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage className="text-center" />
                                </FormItem>
                            )}
                            />
                            <Button 
                                type="submit" 
                                className="w-full h-14 text-lg font-bold glow-effect"
                                disabled={redemptionMutation.isPending}
                            >
                                {redemptionMutation.isPending ? (
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5 mr-2" />
                                        Claim Reward
                                    </>
                                )}
                            </Button>
                        </form>
                    </Form>
                    <AnimatePresence>
                        {redeemStatus && (
                        <motion.div
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            className="overflow-hidden"
                        >
                            <Alert variant={redeemStatus.status === 'success' ? 'default' : 'destructive'} className={`${redeemStatus.status === 'success' ? 'bg-primary/10 border-primary/20 text-primary' : ''}`}>
                                {redeemStatus.status === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                <AlertDescription className="font-semibold">
                                    {redeemStatus.status === 'success' ? `+${redeemStatus.reward} Tokens! ` : ''}{redeemStatus.message}
                                </AlertDescription>
                            </Alert>
                        </motion.div>
                        )}
                    </AnimatePresence>
                </CardContent>
            </Card>
        </motion.div>

        {/* Grid for Active Codes & History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12 items-start">
            {/* Active Promotions */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-1">
                 <Card className="glass-effect border-border h-full">
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <BadgePercent className="w-5 h-5" />
                            <span>Available Codes</span>
                        </CardTitle>
                        <CardDescription>Click any code to automatically claim it.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {isLoadingCodes ? (
                                Array.from({length: 3}).map((_, i) => <div key={i} className="w-full h-24 rounded-lg bg-muted/30 animate-pulse"></div>)
                            ) : activePromoCodes && activePromoCodes.length > 0 ? (
                                activePromoCodes.map((promo) => (
                                    <motion.div
                                        key={promo.id}
                                        whileHover={{ y: -3, transition: { type: 'spring', stiffness: 300 } }}
                                        className="p-4 rounded-lg bg-background/50 border border-border hover:border-primary/50 hover:bg-primary/10 transition-all cursor-pointer"
                                        onClick={() => handleCodeClick(promo.code)}
                                    >
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-md bg-primary/10"><Gift className="h-6 w-6 text-primary" /></div>
                                                <div>
                                                    <p className="font-mono text-lg font-bold tracking-wider text-primary-foreground">{promo.code}</p>
                                                    <p className="text-sm text-muted-foreground">Reward: <span className="font-bold text-accent">{promo.reward} Tokens</span></p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6 text-sm text-center">
                                                <div>
                                                    <p className="font-bold">{`${promo.maxUses - promo.uses}`}</p>
                                                    <p className="text-xs text-muted-foreground">Uses Left</p>
                                                </div>
                                                {promo.expiresAt ? (
                                                <div>
                                                    <p className="font-bold">{formatDistanceToNow(new Date(promo.expiresAt))}</p>
                                                    <p className="text-xs text-muted-foreground">Expires</p>
                                                </div>
                                                ) : <div className="text-xs text-muted-foreground">No Expiry</div>}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="text-center text-muted-foreground p-8">
                                    <Info className="mx-auto w-10 h-10 mb-3"/>
                                    <h3 className="font-semibold text-lg">No Active Codes</h3>
                                    <p>Check our Discord and Twitter for new code drops!</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Redemption History & Socials */}
            <div className="lg:col-span-1 space-y-8">
                 {(isLoadingHistory || (redeemHistory && redeemHistory.length > 0)) && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                        <Card className="glass-effect border-border">
                            <CardHeader>
                                <CardTitle className="flex items-center space-x-2">
                                <History className="w-5 h-5" />
                                <span>Your History</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {isLoadingHistory ? (
                                    <div className="text-sm text-muted-foreground">Loading history...</div>
                                ) : (
                                    <div className="space-y-2 max-h-80 overflow-y-auto pr-2 simple-scrollbar">
                                    {redeemHistory?.map((redemption, index) => (
                                        <motion.div
                                            key={redemption.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.05 * index }}
                                        >
                                        <TransactionItem 
                                            id={redemption.id}
                                            type="promo" 
                                            description={`Code: "${redemption.promoCode?.code || 'DELETED'}"`}
                                            amount={redemption.reward} 
                                            status="completed"
                                            createdAt={new Date(redemption.redeemedAt)} 
                                        />
                                        </motion.div>
                                    ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <Card className="glass-effect border-border">
                        <CardHeader>
                            <CardTitle className="flex items-center text-lg">
                                <Bell className="w-5 h-5 mr-2"/>
                                Stay Updated
                            </CardTitle>
                            <CardDescription>Follow us for exclusive codes!</CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col sm:flex-row gap-4">
                            <Button variant="outline" className="w-full justify-center gap-2 bg-indigo-500/10 border-indigo-500/20 hover:bg-indigo-500/20 text-indigo-400" onClick={() => window.open("https://discord.gg/erHNtCTkpa", "_blank")}>
                                <Zap className="w-5 h-5" /> <span className="font-semibold">Join Discord</span>
                            </Button>
                            <Button variant="outline" className="w-full justify-center gap-2 bg-sky-500/10 border-sky-500/20 hover:bg-sky-500/20 text-sky-400">
                                <Twitter className="w-5 h-5" /> <span className="font-semibold">Follow Twitter</span>
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodes;
